export type FileSizeUnit =
    | 'B'
    | 'kB'
    | 'KiB'
    | 'MB'
    | 'MiB'
    | 'GB'
    | 'GiB'
    | 'TB'
    | 'TiB'
    | 'PB'
    | 'PiB'
    | 'EB'
    | 'EiB'
    | 'ZB'
    | 'ZiB'
    | 'YB'
    | 'YiB';
export type FileSizeUnitSystem = 'SI' | 'EIC';

export const fileSizeUnitFactor: Record<FileSizeUnit, number> = {
    B: 1,
    kB: 10 ** 3,
    KiB: 2 ** 10,
    MB: 10 ** 6,
    MiB: 2 ** 20,
    GB: 10 ** 9,
    GiB: 2 ** 30,
    TB: 10 ** 12,
    TiB: 2 ** 40,
    PB: 10 ** 15,
    PiB: 2 ** 50,
    EB: 10 ** 18,
    EiB: 2 ** 60,
    ZB: 10 ** 21,
    ZiB: 2 ** 70,
    YB: 10 ** 24,
    YiB: 2 ** 80,
};

/**
 * IMPORTANT: Should be in increasing order!
 */
export const systemFileSizeUnits: Record<FileSizeUnitSystem, FileSizeUnit[]> = {
    SI: ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    EIC: ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'],
};

export function fitFileSizeUnit(
    fileSize: number,
    system: FileSizeUnitSystem,
    maximumIntegerDigits = 3
): FileSizeUnit {
    if (fileSize < 0) fileSize = -fileSize;

    const units = systemFileSizeUnits[system];
    for (const unit of units) {
        if (fileSize / fileSizeUnitFactor[unit] < 10 ** maximumIntegerDigits)
            return unit;
    }
    return units[units.length - 1];
}

export type FileSizeOptions = {
    unit?: FileSizeUnit;
    system?: FileSizeUnitSystem;
    maximumIntegerDigits?: number;
    minimumIntegerDigits?: number;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    maximumSignificantDigits?: number;
    unitSeperator?: string;
    locales?: string | string[] | undefined;
    noUnit?: boolean;
};

export function formatFileSize(
    fileSize: number,
    {
        unit,
        system = 'SI',
        maximumIntegerDigits = 3,
        minimumIntegerDigits = 1,
        maximumFractionDigits,
        minimumFractionDigits = 0,
        maximumSignificantDigits = 3,
        unitSeperator = ' ',
        noUnit = false,
        locales,
    }: FileSizeOptions = {}
): string {
    if (Number.isNaN(fileSize)) return 'NaN';

    unit ??= fitFileSizeUnit(fileSize, system, maximumIntegerDigits);

    const digits = Intl.NumberFormat(locales, {
        minimumFractionDigits,
        maximumFractionDigits,
        minimumIntegerDigits,
        maximumSignificantDigits,
        style: 'decimal',
    }).format(fileSize / fileSizeUnitFactor[unit]);

    if (noUnit) return digits;

    return digits + unitSeperator + unit;
}
